import React, { useEffect, useState } from "react";
import { X, MapPin } from "lucide-react";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { CityChooserControls } from "../pages/AccountSettingsPage";

/**
 * iter241 — One-shot post-registration modal that gently nudges a
 * brand-new user to pick their city. Trigger conditions ALL must hold:
 *   (a) user is signed in
 *   (b) user has no city on file
 *   (c) the `cartal-city-onboard-pending` localStorage flag was set by
 *       the register flow (AuthPages.jsx). Ensures the modal ONLY
 *       shows for fresh signups — never for returning users who might
 *       simply not have set a city yet.
 * The flag is cleared the moment the user either saves a city or
 * dismisses the modal, so it never appears again for that account.
 */
const FLAG = "cartal-city-onboard-pending";

export default function CityOnboardingModal() {
  const { user } = useAuth();
  const { lang } = useI18n();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    if (user.city) return;
    let pending = null;
    try { pending = localStorage.getItem(FLAG); } catch { /* private-mode */ }
    if (pending === "1") setOpen(true);
  }, [user]);

  const dismiss = () => {
    try { localStorage.removeItem(FLAG); } catch { /* ignore */ }
    setOpen(false);
  };

  if (!open || !user) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ background: "rgba(10,10,10,0.55)" }}
      role="dialog"
      aria-modal="true"
      data-testid="city-onboarding-modal"
    >
      <div
        className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl"
        style={{ border: "1px solid rgba(0,0,0,0.08)" }}
      >
        <button
          type="button"
          onClick={dismiss}
          className="absolute top-3 end-3 text-[#8a8a8a] hover:text-[#0a0a0a]"
          aria-label={lang === "ar" ? "إغلاق" : "Close"}
          data-testid="city-onboarding-close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-center gap-2 mb-2">
          <MapPin className="w-6 h-6 text-[#8a6f2e]" />
          <h3 className="font-display text-2xl">
            {lang === "ar" ? "حدد مدينتك" : "Set your city"}
          </h3>
        </div>

        <p className="text-[14px] text-[#3a3a3a] leading-relaxed mb-5">
          {lang === "ar"
            ? "خلّ CARTAL أقرب لك 🚗 — حدّد مدينتك لنساعدك في الوصول إلى السيارات والإعلانات والخدمات الأقرب إليك، ونقدم لك تجربة أكثر ملاءمة لمدينتك وفرصًا أقرب لاحتياجاتك."
            : "Bring CARTAL closer to you 🚗 — pick your city and we'll surface the cars, listings, and services nearest to you, with an experience tailored to your area."}
        </p>

        {/* Reuse the SAME chooser used on the account page — dropdown +
            use-my-location + search box. Same save flow, same list. */}
        <CityChooserControls
          lang={lang}
          user={user}
          onSaved={dismiss}
        />

        <button
          type="button"
          onClick={dismiss}
          className="mt-5 w-full text-[13px] text-[#8a8a8a] hover:text-[#0a0a0a]"
          data-testid="city-onboarding-later"
        >
          {lang === "ar" ? "لاحقاً" : "Later"}
        </button>
      </div>
    </div>
  );
}
